import { useAuth } from "react-oidc-context";
import { useCachedResource } from "common-fe";
import { useScoreApi, unwrap } from "../api/scoreApi";
import { useScoreContext } from "../context/ScoreContext";
import { classes } from "../local/db";
import { compareByClassNumber, sortByScheduledTime } from "../util/classDisplay";
import { isAssignedTo } from "../util/scribeAssignment";

// #627 — a scribe's classes are the ones they're assigned to at SOME position;
// that position (not a session-wide one) is how the class gets scored.
// scribeAssignments stores each assignee's ACCOUNT userId (Cognito sub, #396).
// #632 — ordered by scheduledTime (soonest first); ties (or missing time,
// which sorts last) break on class number (#522).
function filterByAssignment(all, sub) {
  const filtered = sub ? all.filter((c) => isAssignedTo(c, sub)) : [];
  return sortByScheduledTime(filtered, compareByClassNumber);
}

/**
 * Classes for a show. Default: filtered to the ones the signed-in scribe is
 * assigned to. Cache-first (#421): served from the IndexedDB classes cache,
 * revalidated from score-api when online.
 *
 * #695 — score-api's listClasses now takes an optional `mine` param that
 * scopes the query SERVER-SIDE to the caller's own scribe assignments (the
 * caller's JWT sub, never a client-supplied id); passing it here means the
 * full per-show class list never travels over the wire or into the local
 * cache for this screen in the first place. The cache read/write is also
 * keyed by (showId, sub) via classes.byShowForScribe/putAllForScribe, so a
 * stale row cached by a DIFFERENT scribe's earlier session on a shared/kiosk
 * device can never surface here either. filterByAssignment below is now a
 * thin defensive net (cheap, harmless) rather than the real narrowing.
 *
 * #694 — pass `{ all: true }` for the "all classes" side of the class-search
 * modal: call listClasses WITHOUT `mine` (every class on the show, incl. ones
 * this scribe isn't assigned to) and use the UNSCOPED cache (putAll/byShow) —
 * the per-scribe cache above holds only "mine", so reading "all" from it would
 * wrongly yield just mine. Same one definition of "mine" as the day sheet: the
 * server's `mine` scoping (+ filterByAssignment net); "all" simply omits it.
 */
export function useClassesForShow(showId, { all: allClasses = false } = {}) {
  const api = useScoreApi();
  const { user } = useAuth();
  const sub = user?.profile?.sub ?? null;
  const { online } = useScoreContext();
  return useCachedResource(
    async () => {
      if (!showId) return [];
      if (allClasses) {
        const cachedAll = await classes.byShow(showId);
        if (!cachedAll.length) return undefined;
        return sortByScheduledTime(cachedAll, compareByClassNumber);
      }
      const cached = await classes.byShowForScribe(showId, sub);
      if (!cached.length) return undefined;
      return filterByAssignment(cached, sub);
    },
    async () => {
      if (!showId) return [];
      if (allClasses) {
        const all = unwrap(await api.classes.listClasses(showId));
        await classes.putAll(all);
        return sortByScheduledTime(all, compareByClassNumber);
      }
      const mine = unwrap(await api.classes.listClasses(showId, sub ? true : undefined));
      await classes.putAllForScribe(mine, sub);
      return filterByAssignment(mine, sub);
    },
    [api, showId, sub, allClasses],
    online,
  );
}
